import {
  type PropsWithChildren,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import { Resizable } from 'react-resizable';
import { Icon } from '@timeedit/ui-components';
import { Card as AntDCard, Tooltip } from '@timeedit/ui-components/antd';
import type {
  CardProps as AntDCardProps,
  CardSemanticStyles,
} from '@timeedit/ui-components/antd/card/index';
import './Card.scss';

export type CardProps = Omit<AntDCardProps, 'styles'> &
  PropsWithChildren<{
    title?: ReactNode;
    tooltip?: string;
    footer?: ReactNode;
    resizableContent?: (size: TSize) => ReactNode;
    styles?: CardSemanticStyles;
  }>;

type TSize = {
  width: number;
  height: number;
};

export const Card = ({
  children,
  title,
  footer,
  tooltip,
  styles,
  resizableContent,
  ...rest
}: CardProps) => {
  const [size, setSize] = useState<TSize>({ width: 500, height: 300 });
  const CardComponent = useMemo(
    () => (
      <AntDCard
        title={title}
        type="inner"
        styles={{
          ...styles,
          body: {
            padding: '10px 0px 10px 16px',
            ...styles?.body,
          },
        }}
        extra={
          tooltip && (
            <Tooltip title={tooltip} zIndex={2001 /* topbar + 1 */}>
              <Icon id="QuestionCircleOutlined" alt="Question Circle" />
            </Tooltip>
          )
        }
        style={{ ...(resizableContent ? size : null) }}
        {...rest}
      >
        {resizableContent
          ? resizableContent({ width: size.width, height: size.height - 100 })
          : children}
        {footer}
      </AntDCard>
    ),
    [children, title, footer, tooltip, styles, size, rest]
  );
  if (!resizableContent) {
    return CardComponent;
  }
  return (
    <Resizable
      {...size}
      onResize={(_e, { size }) => setSize(size)}
      minConstraints={[200, 200]}
    >
      {CardComponent}
    </Resizable>
  );
};
